/**
 * ActivitiesScreen — adaptateur primaire de l'onglet « Activités » du parent.
 *
 * Fil : `useActivitiesData` (l'appel) + `activitiesViewModel` (les deux sections) +
 * `useActivitiesState` (sélection, archivage, destinations) → `<ActivitiesPage>`.
 *
 * La fiche d'une activité et « Générer une activité » sont les deux surfaces posées par-dessus la
 * liste. La fiche est préparée par son crochet et montée avec l'écran (`<ActivityOverlays>`) ; la
 * génération, elle, ne se monte qu'à l'ouverture — d'où le seul état que cet écran possède.
 */
import React, { useCallback, useState } from "react"
import { useSelector } from "react-redux"

import { ActivitiesPage, ConfirmDialog } from "../../../../../../design-system"
import { activitiesViewModel } from "../../../../view-models/parent/Activities/activitiesViewModel"
import { useActivityDetailsFlow } from "../../../hooks/useActivityDetailsFlow"
import { PARENT_TAB_BAR_HEIGHT } from "../ParentTabBar"
import { ActivityOverlays } from "./ActivityOverlays"
import { GenerateActivityOverlay } from "./GenerateActivityOverlay"
import { useActivitiesData } from "./useActivitiesData"
import { useActivitiesState } from "./useActivitiesState"

export function ActivitiesScreen() {
    const { studentId, refresh } = useActivitiesData()
    const vm = useSelector(activitiesViewModel)
    const { selection, archiveConfirm, archiving, snackbar, actions } = useActivitiesState({
        studentId,
        activities: vm.activities,
    })
    const details = useActivityDetailsFlow({
        studentId,
        onChanged: refresh,
        snackbarBottomOffset: PARENT_TAB_BAR_HEIGHT,
    })
    const [generateOpen, setGenerateOpen] = useState(false)
    const closeGenerate = useCallback(() => setGenerateOpen(false), [])

    return (
        <>
            <ActivitiesPage
                activities={vm.activities}
                doneActivities={vm.doneActivities}
                loading={vm.loading}
                refreshing={vm.refreshing}
                error={vm.error}
                onRetry={refresh}
                onRefresh={refresh}
                onOpenActivity={details.open}
                onGenerate={() => setGenerateOpen(true)}
                onOpenArchived={actions.openArchived}
                onSeeAllDone={actions.openRealizations}
                selectionMode={selection.selectionMode}
                selectedIds={selection.selectedIds}
                onStartSelection={actions.startSelection}
                onToggleSelect={actions.toggleSelect}
                onToggleSelectAll={actions.toggleSelectAll}
                onCancelSelection={actions.cancelSelection}
                onArchiveSelected={actions.askArchive}
                archiving={archiving}
            />
            <ConfirmDialog
                visible={archiveConfirm != null}
                title={archiveConfirm?.title ?? ""}
                message={archiveConfirm?.message}
                confirmLabel="Archiver"
                cancelLabel="Annuler"
                onConfirm={actions.confirmArchive}
                onCancel={actions.cancelArchive}
            />
            <ActivityOverlays
                details={details.details}
                notice={details.notice}
                screenNotice={{
                    visible: snackbar != null,
                    message: snackbar?.message ?? "",
                    tone: snackbar?.tone,
                    onDismiss: actions.dismissSnackbar,
                    bottomOffset: PARENT_TAB_BAR_HEIGHT,
                }}
            />
            <GenerateActivityOverlay
                studentId={studentId}
                onCreated={refresh}
                open={generateOpen}
                onClose={closeGenerate}
            />
        </>
    )
}

export default ActivitiesScreen
